import { useEffect, useState } from "react";
import api, { fmtDate, formatApiError } from "@/lib/apiClient";
import AdminLayout from "@/layouts/AdminLayout";
import { amdenNav } from "@/lib/navConfig";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const iso = (d) => d.toISOString().slice(0, 10);
const eur = (n) => `${Number(n || 0) < 0 ? "-" : ""}€${Math.abs(Number(n || 0)).toFixed(2)}`;
const h = (n) => Number(n || 0).toFixed(2);
const STATUS = { approved: "bg-emerald-100 text-emerald-700", exported: "bg-sky-100 text-sky-700", locked: "bg-zinc-200 text-zinc-700" };

function Fig({ f }) {
  if (!f) return <span className="text-zinc-400">—</span>;
  return <span className="text-xs leading-5 block">{h(f.basic)} basic · {h(f.ot15)} at 1.5× · {h(f.ot2)} at 2×<br /><b>{eur(f.total_wages)}</b> wages</span>;
}

function ConfirmDialog({ open, onOpenChange, count, total, onConfirm, busy }) {
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) setReason(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="otr-confirm-dialog">
        <DialogHeader><DialogTitle>Update {count} approved week{count === 1 ? "" : "s"}?</DialogTitle>
          <DialogDescription>Pay figures will be recalculated with the corrected overtime rules (total change {eur(total)}). Weeks stay approved, the client's Admins are notified and each change is saved in Activity Review.</DialogDescription></DialogHeader>
        <Label>Reason</Label>
        <Textarea data-testid="otr-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Client asked to correct Friday overtime" />
        <Button data-testid="otr-confirm" disabled={busy || reason.trim().length < 5} onClick={() => onConfirm(reason)} className="bg-slate-900 text-white">{busy ? "Updating…" : "Confirm update"}</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function OvertimeReview() {
  const [companies, setCompanies] = useState([]);
  const [cid, setCid] = useState("");
  const [start, setStart] = useState(iso(new Date(Date.now() - 91 * 864e5)));
  const [end, setEnd] = useState(iso(new Date()));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState([]);
  const [confirm, setConfirm] = useState(false);
  useEffect(() => { api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {}); }, []);
  const run = async () => {
    setBusy(true); setSel([]);
    try { const { data } = await api.get("/amden/ot-review", { params: { company_id: cid, start, end } }); setData(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const applicable = (data?.rows || []).filter((r) => r.can_apply);
  const toggle = (id) => setSel((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const total = (data?.rows || []).filter((r) => sel.includes(r.id)).reduce((s, r) => s + (r.difference || 0), 0);
  const apply = async (reason) => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/amden/ot-review/apply", { company_id: cid, timesheet_ids: sel, reason });
      toast.success(`Updated ${res.updated.length} week(s)${res.skipped.length ? `, ${res.skipped.length} skipped` : ""}`);
      setConfirm(false); await run();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  return (
    <AdminLayout nav={amdenNav} title="Past Weeks Review">
      <PageHeader title="Past weeks review" subtitle="Approved weeks whose pay would change under the corrected overtime rules. Nothing changes until you confirm." />
      <div className="bg-white border border-zinc-200 rounded-md p-4 flex flex-wrap items-end gap-3 mb-4">
        <div><Label>Client</Label>
          <Select value={cid} onValueChange={(v) => { setCid(v); setData(null); setSel([]); }}><SelectTrigger className="w-64 mt-1" data-testid="otr-company"><SelectValue placeholder="Choose client" /></SelectTrigger>
            <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
        <div><Label>From week</Label><Input data-testid="otr-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 w-44" /></div>
        <div><Label>To week</Label><Input data-testid="otr-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 w-44" /></div>
        <Button data-testid="otr-run" disabled={!cid || busy} onClick={run} className="bg-amber-500 text-black font-bold hover:bg-amber-600">{busy && !data ? "Checking…" : "Check weeks"}</Button>
      </div>
      {data && (
        <div className="bg-white border border-zinc-200 rounded-md">
          <div className="p-3 flex flex-wrap items-center gap-3 border-b border-zinc-100">
            <span className="text-sm" data-testid="otr-summary"><b>{data.rows.filter((r) => !r.error).length}</b> week{data.rows.filter((r) => !r.error).length === 1 ? "" : "s"} would change{data.rows.some((r) => r.error) ? ` · ${data.rows.filter((r) => r.error).length} can't be recalculated` : ""} · {data.unchanged} unchanged</span>
            {applicable.length > 0 && <label className="flex items-center gap-2 text-sm"><Checkbox data-testid="otr-select-all" checked={sel.length === applicable.length} onCheckedChange={(v) => setSel(v ? applicable.map((r) => r.id) : [])} />Select all approved</label>}
            <Button data-testid="otr-apply" disabled={!sel.length || busy} onClick={() => setConfirm(true)} className="ml-auto bg-slate-900 text-white">Update {sel.length || ""} selected</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]">
              <thead><tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500 border-b"><th className="p-3 w-8" /><th className="p-3">Employee</th><th className="p-3">Week</th><th className="p-3">Saved (old)</th><th className="p-3">Corrected (new)</th><th className="p-3 text-right">Difference</th></tr></thead>
              <tbody>
                {data.rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-zinc-400" data-testid="otr-empty">No approved weeks in this range would change.</td></tr>}
                {data.rows.map((r) => (
                  <tr key={r.id} data-testid={`otr-row-${r.id}`} className="border-b border-zinc-100 align-top">
                    <td className="p-3">{r.can_apply && <Checkbox data-testid={`otr-check-${r.id}`} checked={sel.includes(r.id)} onCheckedChange={() => toggle(r.id)} />}</td>
                    <td className="p-3 font-medium">{r.employee_name}<span className={`ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${STATUS[r.status]}`}>{r.status}</span>{r.reviewed && <span className="block text-[11px] text-zinc-400">Updated before</span>}{!r.can_apply && !r.error && <span className="block text-[11px] text-zinc-400">Information only</span>}</td>
                    <td className="p-3">{fmtDate(r.week_start)}</td>
                    <td className="p-3"><Fig f={r.old} /></td>
                    <td className="p-3">{r.error ? <span className="text-xs text-rose-600">{r.error}</span> : <Fig f={r.new} />}</td>
                    <td className={`p-3 text-right font-semibold ${r.difference > 0 ? "text-amber-700" : "text-emerald-700"}`} data-testid={`otr-diff-${r.id}`}>{r.error ? "" : `${r.difference > 0 ? "+" : ""}${eur(r.difference)}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} count={sel.length} total={total} onConfirm={apply} busy={busy} />
    </AdminLayout>
  );
}
